import { inject, Injectable } from "@angular/core";
import { ApiService } from "@services/api.service";
import { DangerError } from "@shared/errors/custom.error";
import { Prestador } from "@models/prestador.model";
import { assignToModel } from "@shared/utils/fieldsMap.utils";
import {
    IResponseAPIListado,
    IResponseAPIObjeto,
} from "@models/responseAPI.interface";
import { Expediente } from "@models/expediente.model";
import { SessionService } from "./session.service";
import ExpedienteEstados from "@shared/enums/expediente-estados.enum";
import { Usuario } from "@models/usuario.model";
import { ExpedienteHistorial } from "@models/expediente-historial.model";
import { Archivo } from "@models/archivo.model";
import { TerminologiaService } from "./terminologia.service";
import { DocumentoService } from "./documento.service";
import TiposDocumento from "@shared/enums/tipos-documento.enum";
import { DiaHabilService } from "./dia-habil.service";
import { obtenerFechaActualConBarras } from "@shared/utils/date.utils";
import { Sancion } from "@models/sancion.model";
import TiposNotificacion from "@shared/enums/notificacion-tipos.enum";
import { NotificacionService } from "./notificacion.service";
import { ApelacionEstados } from "@shared/enums/apelacion-estados.enum";
import { environment } from "@env/environment";
import { GeneradorPdfService } from "./generador-pdf.service";
import IExpediente from "@models/expediente.interface";


interface body {
    [name: string]: string | number;
}

@Injectable({
    providedIn: "root",
})

export class ExpedienteService {
    private readonly mensajeAprobacionExpediente = "Expediente aprobado por presidente";
    private readonly mensajeExpedienteResolucionCompin = "Expediente evaluado, remitido a Presidente COMPIN";
    private readonly mensajeEnvioFiscalizacionExpediente = "Antecedentes enviados a fiscalización";
    private readonly mensajeFirmaResolucion = "Resolución Firmada";
    private readonly mensajeAprobacionApelacion = "Apelación aprobada por presidente";
    private readonly mensajeEnvioApelacion = "Apelación enviada existosamente.";
    private readonly isExpediente: 2 = 2;

    private  readonly apiService = inject(ApiService);
    private  readonly sessionService = inject(SessionService);
    private  readonly terminologiaService = inject(TerminologiaService);
    private  readonly documentoService = inject(DocumentoService);
    private  readonly diaHabilService = inject(DiaHabilService);
    private  readonly notificacionService = inject(NotificacionService);
    private  readonly generadorPdfService = inject(GeneradorPdfService);

    private get rutSinDV(): number {
        return this.sessionService.getSesionActual()?.getRutSinDV() ?? 0;
    }

    private get cobertura(): any {
        return this.sessionService.getSesionActual()?.cobertura;
    }


    async obtenerExpedientes(cod_estado?: string, prestador: boolean = false, filtros: any = {}): Promise<Expediente[]> {
        const body: body = { cobertura: this.cobertura };
        if (cod_estado) body.cod_estado = cod_estado;
        if (prestador) body.run = this.rutSinDV;

        if (filtros && Object.keys(filtros).length > 0) {
            body.cod_estado = filtros.estados;
            body.regiones = filtros.regiones;
            body.expediente = filtros.expediente;
            body.run = filtros.rut;
        }

        const { data, msg, success } =
            await this.apiService.privatePost<IResponseAPIListado>(
                `listar-expedientes`,
                body
            );

        if (!success || data.length === 0) throw new DangerError(msg);
        return data.map((e: any) => {
            e.comunaPrestador = {
                nombre: e.pre_comuna
            },
                e.prestador = assignToModel(Prestador, e, {
                    nombres: "nombre_prestador",
                    email_fiscalizacion: "pre_email_fis",
                    direccion: "pre_direccion",
                    email: "pre_email_lm",
                    rut: "pre_rut",
                    dv: "pre_rutdv",
                    telefono: "pre_telefono",
                    comuna: "comunaPrestador"
                });
            e.estado = {
                codigo: e.eex_codigo,
                nombre: e.eex_nombre,
            };
            e.region = {
                codigo: e.reg_codigo,
                nombre: e.reg_nombre,
            };
            e.motivo = {
                nombre: e.exp_motivo_prorroga,
                archivo: assignToModel(Archivo, e, { ruta: "exp_ruta_prorroga" })
            }
            e.porcentajesDocumentos = {
                obligatoriosIngresados: e.doctos_oblig_ingresados,
                obligatoriosValidados: e.doctos_oblig_validados,
                opcionalesValidados: e.doctos_opcio_validados,
            };
            // Mapear sanción si viene en el response
            if (e.sancion) {
                e.sancionModel = assignToModel(Sancion, e.sancion, {
                    id: "exs_id",
                    porcentaje: "porcentaje_cumplimiento",
                    monto: "monto",
                    dias: "dias",
                    pasada: "pasada",
                    sancion_previa: "syn_codigo_sancion_previa",
                    fecha: "fecha_creacion"
                });
            }

            return assignToModel(Expediente, e, {
                id: "exp_id",
                cantidad_licencias: "exp_cantidad_licencias",
                fecha_cohorte: "exp_fecha_cohorte",
                fecha_creacion: "exp_fecha_creacion",
                fecha_termino: "exe_fecha_termino",
                exp_cierra_expediente: "exp_cierra_expediente",
                iteracion: "exp_pasada_sancion",
                cumplimiento: "exp_porcentaje_cumplimiento",
                estado_revision: "exp_antecedentes",
                sancion: "sancionModel",
                devoluciones: "exp_devoluciones"
            });

        });
    }

    async obtenerExpedientesEstados(idExpediente: number): Promise<ExpedienteHistorial[]> {
        const { data, msg, success } =
            await this.apiService.privatePost<IResponseAPIListado>(
                `expedientes-detalle-historial`,
                {
                    idExpediente,
                }
            );

        if (!success || data.length === 0) throw new DangerError(msg);
        return data.map((e: any) => {
            e.estado = { codigo: e.eex_codigo, nombre: e.eex_nombre };
            e.estadoAnterior = { codigo: e.eex_codigo_anterior, nombre: e.eex_nombre_anterior };
            e.UsuarioCrea = assignToModel(Usuario, e, { rut_usuario: "usu_rut_crea", nombres: "usu_nombre" });
            e.UsuarioModifica = assignToModel(Usuario, e, { rut_usuario: "usu_rut_modifica", nombres: "usu_nombre_modifica" });
            return assignToModel(ExpedienteHistorial, e, {
                id: "exe_id",
                fecha_inicio: "exe_fecha_ingreso",
                fecha_limite: "exe_fecha_termino",
                fecha_termino: "exe_cambio_estado",
                descripcion: "eex_descripcion",
                cambio_manual: "tipo_cambio"
            });
        });
    }

    async obtenerExpedientesPorRut(): Promise<Expediente[]> {
        const cod_estado = `${ExpedienteEstados.aprobado},${ExpedienteEstados.solicitud_prorroga},${ExpedienteEstados.prorroga_aprobada},${ExpedienteEstados.prorroga_rechazada}`;
        return this.obtenerExpedientes(cod_estado, true);
    }

    async obtenerExpedientesAprobados(): Promise<Expediente[]> {
        const { data, msg, success } =
            await this.apiService.privatePost<IResponseAPIListado>(
                `listar-expedientes-aprobados`
            );

        if (!success || data.length === 0) throw new DangerError(msg);
        return data.map((e: any) => {
            e.prestador = assignToModel(Prestador, e, {
                nombres: "nombre_prestador",
            });
            e.estado = {
                id: e.eex_codigo,
                nombre: e.eex_nombre,
            };
            return assignToModel(Expediente, e, {
                id: "exp_id",
                cantidad_licencias: "exp_cantidad_licencias",
                fecha_cohorte: "exp_fecha_cohorte",
                fecha_creacion: "exp_fecha_creacion",
            });
        });
    }

    private async cambiarEstadoExpediente(
        idExpediente: number,
        estadoActual: number,
        estadoNuevo: number,
        tipoNotificacion: TiposNotificacion,
        motivo?: any,
        mrxCodigo?: number,
        justificacion?: string
    ) {
        const body: any = {
            idExpediente,
            rutUsuario: this.rutSinDV,
            estadoActual,
            estadoNuevo,
            motivo,
            tipoNotificacion
        };
        // RFC1: motivo estructurado de rechazo (catálogo cerrado) + justificación ("Otro")
        if (mrxCodigo !== undefined) body.mrxCodigo = mrxCodigo;
        if (justificacion !== undefined) body.justificacion = justificacion;

        const { msg, success } =
            await this.apiService.privatePost<IResponseAPIObjeto>(
                `expedientes-cambiar-estado`,
                body
            );

        if (!success) throw new DangerError(msg);
        return msg;
    }


    /*  
    ----------------------------------------------------------
    1. El Presidente aprueba la solicitud de antecedentes y  
    emite el Ordinario - Solicitud de antecedentes
    ----------------------------------------------------------
    */

    async aprobarExpediente(idExpediente: number): Promise<string> {
        /*  
        ----------------------------------------------------------
        1.1. Se genera el documento Ordinario - Solicitud de 
        antecedentes
        ----------------------------------------------------------
        */

        const dataPdf = await this.generadorPdfService.generarPdf(
            idExpediente,
            1,
            this.rutSinDV
        );

        if (!dataPdf || !dataPdf.key) {
            throw new DangerError("No se pudo obtener la URL del documento generado.");
        }

        /*  
        ----------------------------------------------------------
        1.2. Se cambia el estado del expediente.
        ----------------------------------------------------------
        */
        const msg = await this.cambiarEstadoExpediente(
            idExpediente,
            ExpedienteEstados.ingresado,
            ExpedienteEstados.aprobado,
            null,
            this.mensajeAprobacionExpediente
        );

        /*  
        ----------------------------------------------------------
        1.3. Se notifica a través de correo electrónico el 
        ordinario generado.
        ----------------------------------------------------------
        */
        await this.notificacionService.generarNotificacion(
            idExpediente,
            TiposNotificacion.aprobacion_expediente,
            dataPdf.key
        );

        return msg;
    }

    /*
    ----------------------------------------------------------
    RFC1. El Presidente rechaza el expediente seleccionando un motivo
    predeterminado (catálogo cerrado). Si el motivo es "Otro", se exige
    justificación (máx. 250 caracteres).
    ----------------------------------------------------------
    */
    async rechazarExpediente(idExpediente: number, mrxCodigo: number, justificacion?: string): Promise<string> {
        // El motivo legible se persiste en el historial del expediente (columna Observación).
        const motivoSeleccionado = this.terminologiaService
            .obtenerMotivosRechazoExpediente()
            .find(m => m.codigo === mrxCodigo);
        const motivo = justificacion
            ? `${motivoSeleccionado?.nombre ?? "Rechazo"}: ${justificacion}`
            : motivoSeleccionado?.nombre ?? "Expediente rechazado por presidente";

        const msg = this.cambiarEstadoExpediente(
            idExpediente,
            ExpedienteEstados.ingresado,
            ExpedienteEstados.rechazado,
            TiposNotificacion.solicitud_antecedentes,
            motivo,
            mrxCodigo,
            justificacion ?? null
        );
        return msg;
    }

    async enviarAntecedentes(idExpediente: number, estadoActual: number): Promise<string> {
        const msg = this.cambiarEstadoExpediente(idExpediente, estadoActual, ExpedienteEstados.enviado_fiscalizacion, TiposNotificacion.enviar_antecedentes, this.mensajeEnvioFiscalizacionExpediente);
        return msg;
    }

    /*
    ----------------------------------------------------------
    RFC2/RFC4. El Presidente devuelve el expediente al fiscalizador
    por un error detectado en la evaluación.
    ----------------------------------------------------------
    */
    async devolverExpediente(idExpediente: number, estadoActual: number, observacion: string): Promise<string> {
        /*
        ----------------------------------------------------------
        RFC4. Se cambia el estado del expediente a "en corrección"
        siguiendo el mismo patrón que el resto de las transiciones
        de estado (cambiarEstadoExpediente + notificación al fiscalizador).
        ----------------------------------------------------------
        */
        const msg = await this.cambiarEstadoExpediente(
            idExpediente,
            estadoActual,
            ExpedienteEstados.en_correccion,
            TiposNotificacion.devolver_expediente,
            observacion
        );

        return msg;
    }



    async respuestaApelacionExpediente(
        idExpediente: number,
        archivo: Archivo | null,
        motivo: string | null,
        estadoApelacion: ApelacionEstados,
        estadoApelacionParcial: string
    ): Promise<string> {
        const tipoDocumento = this.terminologiaService.obtenerTipoDocumentoResultadoApelacion();
        const { data, msg, success } = await this.apiService.privatePost<IResponseAPIListado>(
            `apelaciones-respuesta`,
            {
                idExpediente,
                base64: archivo?.base64,
                nombre: archivo?.nombre,
                tipoDocumento,
                rutUsuario: this.rutSinDV,
                estadoNuevo: ExpedienteEstados.apelacion_suseso,
                estadoApelacion,
                estadoApelacionParcial,
                motivo
            }
        );
        if (!success) throw new DangerError(msg);
        return msg;
    }

    async rechazarApelacionExpediente(
        idExpediente: number,
        archivo: Archivo | null,
        motivo: string | null
    ):
        Promise<string> {
        let data: any, msg: string, success: boolean;
        const tipoDocumento = this.terminologiaService.obtenerTipoDocumentoResultadoApelacion();
        if (archivo) {
            const response = await this.apiService.privatePost<IResponseAPIListado>(
                `expediente-subir-documento`,
                {
                    idExpediente,
                    base64: archivo.base64,
                    nombre: archivo.nombre,
                    tipoDocumento,
                    run: this.rutSinDV,
                    iteracion: 1 // TODO: Agregar variable cuando se esten manejando las iteraciones
                }
            );
            ({ data, msg, success } = response);
            const errorMsg: string = (data[0] as unknown as string) ?? msg
            if (!success) throw new DangerError(errorMsg);
        }

        const msgEstado = this.cambiarEstadoExpediente(idExpediente, ExpedienteEstados.resolucion_apelada, ExpedienteEstados.apelacion_rechazada, TiposNotificacion.solicitud_antecedentes, motivo);
        return msgEstado;
    }

    async enviarApelacionExpediente(idExpediente: number): Promise<string> {
        const msg = this.cambiarEstadoExpediente(idExpediente, ExpedienteEstados.resolucion_firmada, ExpedienteEstados.resolucion_apelada, TiposNotificacion.solicitud_antecedentes, this.mensajeEnvioApelacion);
        return msg;
    }

    async crearExpediente(
        licenciasSeleccionadas: string[],
        prestador: Prestador,
        region: number,
        email: string
    ): Promise<string> {
        const body: any = {
            rut: this.rutSinDV,
            licenciasSeleccionadas,
            prestador,
            region,
        };
        if (email) body.email = email;

        const { msg, success } =
            await this.apiService.privatePost<IResponseAPIObjeto>(
                `crear-expediente`,
                body
            );

        if (!success) throw new DangerError(msg);

        // Extraer número de expediente aquí
        const numeroExpediente = this.extraerNumeroExpediente(msg); // helper interno del servicio

        if (numeroExpediente) {
            try {
                await this.notificacionService.generarNotificacion(
                    numeroExpediente,
                    TiposNotificacion.creacion_expediente // = 1
                );
            } catch (e) {
                console.error("Error al enviar notificación de creación de expediente", e);
                // opcional: no relanzar error
            }
        }

        return msg;
    }

    private extraerNumeroExpediente(mensaje: string): number | null {
        if (!mensaje) return null;
        // Ej: "El expediente N°230 ha sido creado correctamente"
        const match = mensaje.match(/expediente\s*N[º°]?\s*(\d+)/i);
        return match ? Number(match[1]) : null;
    }

    async solicitarProrrogaExpediente(
        idExpediente: number,
        archivo: Archivo,
        motivo: string
    ): Promise<string> {
        const tipoDocumento = this.terminologiaService.obtenerTipoDocumentoProrroga();

        const { data, msg, success } =
            await this.apiService.privatePost<IResponseAPIListado>(
                `expediente-subir-documento`,
                {
                    idExpediente,
                    base64: archivo.base64,
                    nombre: archivo.nombre,
                    tipoDocumento,
                    run: this.rutSinDV,
                    iteracion: 1
                }
            );

        const errorMsg: string = (data[0] as unknown as string) ?? msg;
        if (!success) throw new DangerError(errorMsg);

        // 1) Cambio de estado + NOTI 4 (lo maneja la lambda expedientes-cambiar-estado)
        const msgEstado = await this.cambiarEstadoExpediente(
            idExpediente,
            ExpedienteEstados.aprobado,
            ExpedienteEstados.solicitud_prorroga,
            TiposNotificacion.prestador_prorroga,   // = 4
            motivo
        );

        // 2) Si el cambio y la noti 4 salen OK, enviamos NOTI 5 desde el front usando el servicio
        try {
            await this.notificacionService.generarNotificacion(
                idExpediente,
                TiposNotificacion.presidente_prorroga  // = 5
            );
        } catch (e) {
            console.error("Error al crear notificación presidente_prorroga", e);
            // opcional: no romper la UX si la noti 5 falla
        }

        return msgEstado;
    }

    /*  
    ----------------------------------------------------------
    3. Firma de la resolución del expediente.
    ----------------------------------------------------------
    */
    async firmarResolucion(idExpediente: number, estadoActual: number, templateId: number = 3): Promise<{ msg: string; tde_codigo: number }> {

    /*  
    ----------------------------------------------------------
    3.1. Se genera el documento según la resolución de la 
    sanción del expediente.
    ----------------------------------------------------------
    */
        const dataPdf = await this.generadorPdfService.generarPdf(
            idExpediente,
            templateId,
            this.rutSinDV
        );

    /*  
    ----------------------------------------------------------
    3.2. Actualización del estado del expediente.
    ----------------------------------------------------------
    */
        const msg = await this.cambiarEstadoExpediente(
            idExpediente,
            estadoActual,
            ExpedienteEstados.resolucion_firmada,
            null,
            this.mensajeFirmaResolucion
        );

    /*  
    ----------------------------------------------------------
    3.3. Enviar correo electrónico con el documento adjunto.
    ----------------------------------------------------------
    */
        if (dataPdf?.key) {
            await this.notificacionService.generarNotificacion(
                idExpediente,
                TiposNotificacion.firmar_resolucion,
                dataPdf.key
            );
        }

        return { msg, tde_codigo: dataPdf?.tde_codigo ?? 3 };
    }

    async descargarResolucionFirmada(idExpediente: number, tdeCodigo: number = 3): Promise<void> {
        const nombreArchivo = `resolucion_expediente_${idExpediente}.pdf`;
        const { data, msg, success } = await this.documentoService.obtenerDocumentos(
            this.isExpediente,
            idExpediente,
            tdeCodigo,
        );

        if (!success || !data.length) {
            throw new DangerError(msg || 'No se encontró la resolución firmada.');
        }

        // Tomar el documento más reciente (último de la lista)
        const archivo = data[0];
        const base64 = archivo?.s3?.base64;

        if (!base64) {
            throw new DangerError('La respuesta no contiene el archivo en base64.');
        }

        const contentType = archivo?.s3?.type || 'application/pdf';
        this.descargarPdfDesdeBase64(base64, contentType, nombreArchivo);
    }

    private descargarPdfDesdeBase64(base64: string, contentType: string, nombreArchivo: string): void {
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: contentType });
        const blobUrl = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = nombreArchivo;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
    }

    async cerrarExpediente(expediente: Expediente): Promise<string> {
        // Bug fix: al cerrar el expediente (estado "Expediente a resolución COMPIN")
        // la observación no debe indicar "Expediente aprobado por presidente", ya que
        // esta transición la ejecuta el fiscalizador al terminar su evaluación.
        const msg = this.cambiarEstadoExpediente(expediente.id, expediente.estado.codigo, ExpedienteEstados.cerrado, TiposNotificacion.cerrar_expediente, this.mensajeExpedienteResolucionCompin);
        return msg;
    }


    /*  
    ----------------------------------------------------------
    2. Se aprueba la solicitud de ampliación de plazo de 
    entrega de los antecedentes.
    ----------------------------------------------------------
    */
    async aprobarRechazarExpediente(
        idExpediente: number,
        estadoActual: number,
        estadoNuevo: number,
        motivo: string | null
    ): Promise<string> {
        let tipoNotificacion: TiposNotificacion | null = null;
        let motivoFinal: string;
        let dataPdf: any = null; // Variable para almacenar la URL del PDF

        if (estadoNuevo === ExpedienteEstados.prorroga_aprobada) {
            tipoNotificacion = TiposNotificacion.aprobacion_prorroga; // Esto es igual a 6
            motivoFinal = motivo || "Prórroga aprobada por presidente";

           
    /*  
    ----------------------------------------------------------
    2.1. Se genera el documento Ordinario - Sobre solicitud
    de ampliación de plazo
    ----------------------------------------------------------
    */
            dataPdf = await this.generadorPdfService.generarPdf(
                idExpediente,
                2,
                this.rutSinDV
            );

            if (!dataPdf || !dataPdf.url) {
                throw new DangerError("No se pudo obtener la URL del documento de prórroga.");
            }

        } else if (estadoNuevo === ExpedienteEstados.prorroga_rechazada) {
            tipoNotificacion = TiposNotificacion.rechazo_prorroga;    // = 7
            motivoFinal = motivo || "Prórroga rechazada por presidente";
        } else {
            // Otros usos de este método
            motivoFinal = motivo || "Cambio de estado de expediente";
        }

    /*  
    ----------------------------------------------------------
    2.2 Se actualiza el estado del expediente.
    ----------------------------------------------------------
    */
        const msg = await this.cambiarEstadoExpediente(
            idExpediente,
            estadoActual,
            estadoNuevo,
            tipoNotificacion,
            motivoFinal
        );

    /*  
    ----------------------------------------------------------
    2.3. Se envia el correo electrónico con el documento 
    generado.
    ----------------------------------------------------------
    */
        if (estadoNuevo === ExpedienteEstados.prorroga_aprobada && dataPdf?.key) {
            await this.notificacionService.generarNotificacion(
                idExpediente,
                tipoNotificacion,
                dataPdf.key    
            );
        }

        return msg;
    }


    async obtenerSanciones(idExpediente: number): Promise<Sancion[]> {
        const { data, success } = await this.apiService.privatePost<IResponseAPIListado>(
            `expedientes-sanciones`, { idExpediente }
        );
        if (!success) return [];
        return data.map((e: any) => {
            return assignToModel(Sancion, e, {
                id: "codigo_sancion",
                fecha: "fecha_sancion",
                pasada: "pasada_sancion",
                porcentaje: "porcentaje_cumplimiento",
                monto: "monto_sancion",
                dias: "dias_sancion"
            });
        });
    }

    async obtenerSancionPorIteracion(idExpediente: number, iteracion: number): Promise<Sancion> {
        const sanciones = await this.obtenerSanciones(idExpediente);
        return sanciones.find(s => s.pasada === iteracion);
    }

    async obtenerDocumentos(idExpediente: number, mostrarError: boolean = true): Promise<Archivo[]> {
        const { data, msg, success } = await this.documentoService.obtenerDocumentos(this.isExpediente, idExpediente);

        if (!success || data.length === 0) {
            if (mostrarError) throw new DangerError(msg);
            return;
        }
        return data;
    }

    async obtenerDocumentoProrroga(idExpediente: number): Promise<Archivo> {
        const { data, msg, success } = await this.documentoService.obtenerDocumentos(this.isExpediente, idExpediente, TiposDocumento.solicitud_prorroga);

        if (!success || data.length === 0) throw new DangerError(msg);
        return data[0];
    }

    async obtenerDocumentoApelacion(idExpediente: number, mostrarError: boolean = false): Promise<Archivo | undefined> {
        const { data, msg, success } = await this.documentoService.obtenerDocumentos(this.isExpediente, idExpediente, TiposDocumento.solicitud_apelacion);

        if (!success || data.length === 0) {
            if (mostrarError) throw new DangerError(msg);
            return;
        }
        return data[0];
    }

    async obtenerDiasHabilesRestantes(fechaTermino: string, mostrarError: boolean = true): Promise<number> {
        const { data, msg, success } = await this.diaHabilService.obtenerDiasHabiles(obtenerFechaActualConBarras(), fechaTermino);

        if (!success) {
            if (mostrarError) throw new DangerError(msg);
            return;
        }
        return data;
    }

    async marcarComoApelado(idExpediente: number): Promise<string> {
        try {
            console.log("Marcar como apelado - servicio expediente");
            await this.cambiarEstadoExpediente(
                idExpediente,
                ExpedienteEstados.resolucion_firmada,
                ExpedienteEstados.apelacion_suseso,
                TiposNotificacion.apelacion_suseso,
                "Expediente marcado con apelación"
            );
            // Crear registro de apelación con estado "creada" usando el método existente
            const msg = await this.respuestaApelacionExpediente(
                idExpediente,
                null, // archivo
                "Apelación creada", // motivo
                ApelacionEstados.creada, // estadoApelacion
                null // estadoApelacionParcial
            );
            return msg;
        } catch (error) {
            console.log("Error al marcar como apelado - servicio expediente", error);
        }
    }
}